import { useState } from "react";
import "./admin.css";
import { usePartnerReport } from "@/hooks/queries/usePartnerReport";
import type { PartnerReportRow } from "@/hooks/queries/usePartnerReport";

const PERIODS = [7, 30, 90] as const;
type Period = (typeof PERIODS)[number];

function rate(numerator: number, denominator: number): string {
  if (!denominator) return "—";
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

export default function PartnerReports() {
  const [period, setPeriod] = useState<Period>(30);
  const { rows, isLoading, isError, error, refetch } = usePartnerReport(period);

  const sorted = [...rows].sort(
    (a, b) => b.paying_conversions - a.paying_conversions
  );

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Partner reports</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Outbound funnel (Stash) + inbound funnel (QR scans → signups → paying).
          </p>
        </div>
        <div className="flex items-center gap-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                period === p
                  ? "bg-orange-600 text-white"
                  : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {p}d
            </button>
          ))}
        </div>
      </div>

      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 mb-4">
          Failed to load report:{" "}
          {error?.message ?? "unknown error"}{" "}
          <button
            onClick={() => void refetch()}
            className="underline ml-2"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : sorted.length === 0 && !isError ? (
        <div className="rounded-lg border border-dashed border-gray-200 p-12 text-center text-sm text-gray-400">
          No partner data for this period yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="admin-table min-w-full">
            <thead>
              <tr>
                <th rowSpan={2} className="align-bottom">Partner</th>
                <th colSpan={6} className="text-center border-l border-gray-200 bg-blue-50 text-blue-700">
                  ← Outbound (Stash)
                </th>
                <th colSpan={6} className="text-center border-l border-gray-200 bg-green-50 text-green-700">
                  Inbound (QR) →
                </th>
              </tr>
              <tr>
                {/* Outbound */}
                <th className="border-l border-gray-200">Impressions</th>
                <th>Detail opens</th>
                <th>Clickouts</th>
                <th>Uniq users</th>
                <th>Purchases</th>
                <th>Redemptions</th>
                {/* Inbound */}
                <th className="border-l border-gray-200">Scans</th>
                <th>Uniq sessions</th>
                <th>Signups</th>
                <th>Paying</th>
                <th>Scan→signup</th>
                <th>Signup→paying</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <ReportRow key={row.partner_id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-gray-400">
        Data updated daily at 04:00 UTC. Per-surface breakdown available in CSV export (coming soon).
      </p>
    </div>
  );
}

function ReportRow({ row }: { row: PartnerReportRow }) {
  return (
    <tr>
      <td>
        <div className="font-medium text-gray-900">{row.branding_title}</div>
        <div className="text-xs text-gray-400">{row.legal_name}</div>
      </td>
      {/* Outbound */}
      <td className="border-l border-gray-100 text-right">{row.shop_impressions.toLocaleString()}</td>
      <td className="text-right">
        {row.shop_detail_opens.toLocaleString()}
        <span className="ml-1 text-xs text-gray-400">
          {rate(row.shop_detail_opens, row.shop_impressions)}
        </span>
      </td>
      <td className="text-right">
        {row.partner_clickouts.toLocaleString()}
        <span className="ml-1 text-xs text-gray-400">
          {rate(row.partner_clickouts, row.shop_detail_opens)}
        </span>
      </td>
      <td className="text-right">{row.unique_users_reached.toLocaleString()}</td>
      <td className="text-right">
        {row.purchases.toLocaleString()}
        <span className="ml-1 text-xs text-gray-400">
          {rate(row.purchases, row.partner_clickouts)}
        </span>
      </td>
      <td className="text-right">
        {row.redemptions.toLocaleString()}
        <span className="ml-1 text-xs text-gray-400">
          {rate(row.redemptions, row.purchases)}
        </span>
      </td>
      {/* Inbound */}
      <td className="border-l border-gray-100 text-right">{row.scan_events.toLocaleString()}</td>
      <td className="text-right">{row.unique_scan_sessions.toLocaleString()}</td>
      <td className="text-right">
        {row.signups.toLocaleString()}
        <span className="ml-1 text-xs text-gray-400">
          {rate(row.signups, row.unique_scan_sessions)}
        </span>
      </td>
      <td className="text-right">
        {row.paying_conversions.toLocaleString()}
        <span className="ml-1 text-xs text-gray-400">
          {rate(row.paying_conversions, row.signups)}
        </span>
      </td>
      <td className="text-right font-medium text-green-700">
        {rate(row.signups, row.unique_scan_sessions)}
      </td>
      <td className="text-right font-medium text-green-700">
        {rate(row.paying_conversions, row.signups)}
      </td>
    </tr>
  );
}
